import { useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook do tworzenia powiadomień z wbudowanym mechanizmem anti-spam
 * Zapobiega zbyt częstym powiadomieniom od tego samego użytkownika/typu
 * 
 * @param {Object} options - Opcje konfiguracyjne
 * @param {number} options.cooldownMinutes - Czas cooldown w minutach (domyślnie 5)
 * @returns {Function} createNotification - Funkcja do tworzenia powiadomienia
 */
export function useCreateNotification(options = {}) {
  const { cooldownMinutes = 5 } = options;
  const cooldownMapRef = useRef(new Map()); // Map<key, timestamp>

  /**
   * Tworzy klucz unikalny dla powiadomienia (do sprawdzania cooldown)
   * @param {string} userId - ID użytkownika, który otrzyma powiadomienie
   * @param {string} type - Typ powiadomienia
   * @param {string} uniqueKey - Dodatkowy klucz (np. sender_id dla friend_invitation)
   * @returns {string} Klucz unikalny
   */
  const createCooldownKey = (userId, type, uniqueKey = '') => {
    return `${userId}_${type}_${uniqueKey}`;
  };

  /**
   * Sprawdza czy można wysłać powiadomienie (czy minął cooldown)
   * @param {string} userId - ID użytkownika, który otrzyma powiadomienie
   * @param {string} type - Typ powiadomienia
   * @param {string} uniqueKey - Dodatkowy klucz
   * @returns {boolean} true jeśli można wysłać, false jeśli jest cooldown
   */
  const canSendNotification = useCallback((userId, type, uniqueKey = '') => {
    const key = createCooldownKey(userId, type, uniqueKey);
    const lastSent = cooldownMapRef.current.get(key);
    
    if (!lastSent) {
      return true; // Nie było wcześniej wysyłane, można wysłać
    }

    const cooldownMs = cooldownMinutes * 60 * 1000;
    const timeSinceLastSent = Date.now() - lastSent;
    
    return timeSinceLastSent >= cooldownMs;
  }, [cooldownMinutes]);

  /**
   * Oznacza że powiadomienie zostało wysłane (zapisuje timestamp)
   * @param {string} userId - ID użytkownika, który otrzymał powiadomienie
   * @param {string} type - Typ powiadomienia
   * @param {string} uniqueKey - Dodatkowy klucz
   */
  const markNotificationSent = useCallback((userId, type, uniqueKey = '') => {
    const key = createCooldownKey(userId, type, uniqueKey);
    cooldownMapRef.current.set(key, Date.now());

    // Cleanup starych wpisów (starsze niż 24h) - żeby nie zaśmiecać pamięci
    const cleanupInterval = 24 * 60 * 60 * 1000; // 24 godziny
    const now = Date.now();
    for (const [mapKey, timestamp] of cooldownMapRef.current.entries()) {
      if (now - timestamp > cleanupInterval) {
        cooldownMapRef.current.delete(mapKey);
      }
    }
  }, []);

  /**
   * Sprawdza w bazie danych czy podobne powiadomienie nie zostało już niedawno wysłane
   * Dodatkowa ochrona na poziomie bazy danych
   * @param {string} userId - ID użytkownika
   * @param {string} type - Typ powiadomienia
   * @param {string} uniqueKey - Unikalny klucz do identyfikacji (np. sender_id dla friend_invitation)
   * @param {number} minutesThreshold - Próg w minutach (domyślnie 5)
   * @returns {Promise<boolean>} true jeśli można wysłać, false jeśli jest w bazie
   */
  const checkDatabaseCooldown = async (userId, type, uniqueKey = '', minutesThreshold = 5) => {
    try {
      const thresholdDate = new Date(Date.now() - minutesThreshold * 60 * 1000).toISOString();
      
      // Pobierz wszystkie powiadomienia tego typu dla użytkownika z ostatnich X minut
      const { data, error } = await supabase
        .from('notifications')
        .select('created_at, data')
        .eq('user_id', userId)
        .eq('type', type)
        .gte('created_at', thresholdDate)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error checking database cooldown:', error);
        // W przypadku błędu, pozwalamy na wysłanie (fail-safe)
        return true;
      }

      // Jeśli nie ma żadnych powiadomień, można wysłać
      if (!data || data.length === 0) {
        return true;
      }

      // Jeśli mamy uniqueKey, sprawdzamy czy w data jest ten sam klucz
      // Np. dla friend_invitation sprawdzamy data->sender_id
      if (uniqueKey) {
        // Sprawdź czy jest już powiadomienie z tym samym uniqueKey w data
        const hasMatchingKey = data.some(notif => {
          if (!notif.data || typeof notif.data !== 'object') return false;
          // Sprawdź różne możliwe pola w data
          return notif.data.sender_id === uniqueKey || 
                 notif.data.friend_id === uniqueKey ||
                 notif.data.applicant_id === uniqueKey ||
                 notif.data.user_id === uniqueKey;
        });
        
        // Jeśli znaleziono podobne powiadomienie, nie można wysłać
        return !hasMatchingKey;
      }

      // Jeśli nie ma uniqueKey, ale są jakieś powiadomienia tego typu, nie można wysłać
      // (zapobiega spamowi tego samego typu)
      return false;
    } catch (error) {
      console.error('Error in checkDatabaseCooldown:', error);
      return true; // Fail-safe - pozwalamy na wysłanie w przypadku błędu
    }
  };

  /**
   * Tworzy powiadomienie z mechanizmem anti-spam
   * 
   * @param {Object} params - Parametry powiadomienia
   * @param {string} params.userId - ID użytkownika, który otrzyma powiadomienie
   * @param {string} params.type - Typ powiadomienia (friend_invitation, application_received, etc.)
   * @param {Object} params.data - Dane powiadomienia (JSONB)
   * @param {string} params.eventId - ID eventu (opcjonalne)
   * @param {string} params.applicationId - ID zgłoszenia (opcjonalne)
   * @param {string} params.uniqueKey - Unikalny klucz dla anti-spam (np. sender_id dla friend_invitation)
   * @param {boolean} params.skipCooldown - Pomiń sprawdzanie cooldown (używaj ostrożnie)
   * @returns {Promise<{success: boolean, notificationId?: string, error?: Error, skipped?: boolean, reason?: string}>}
   */
  const createNotification = useCallback(async (params) => {
    const {
      userId,
      type,
      data = {},
      eventId = null,
      applicationId = null,
      uniqueKey = '',
      skipCooldown = false
    } = params;

    if (!userId || !type) {
      return { 
        success: false, 
        error: new Error('userId and type are required'),
        skipped: false
      };
    }

    // Sprawdź cooldown (jeśli nie pomijamy)
    if (!skipCooldown) {
      // 1. Sprawdź lokalny cooldown (w pamięci)
      if (!canSendNotification(userId, type, uniqueKey)) {
        const lastSent = cooldownMapRef.current.get(createCooldownKey(userId, type, uniqueKey));
        const timeRemaining = Math.ceil(((lastSent + cooldownMinutes * 60 * 1000) - Date.now()) / 1000 / 60);
        return {
          success: false,
          skipped: true,
          reason: `Cooldown aktywny. Poczekaj ${timeRemaining} minut(y).`
        };
      }

      // 2. Sprawdź cooldown w bazie danych (dodatkowa ochrona)
      const canSendFromDB = await checkDatabaseCooldown(userId, type, uniqueKey, cooldownMinutes);
      if (!canSendFromDB) {
        // Aktualizuj lokalny cooldown map żeby nie próbować ponownie
        markNotificationSent(userId, type, uniqueKey);
        return {
          success: false,
          skipped: true,
          reason: 'Podobne powiadomienie zostało już niedawno wysłane.'
        };
      }
    }

    try {
      // Tworzymy powiadomienie używając funkcji z bazy danych lub bezpośredniego INSERT
      let notificationId = null;

      // Próbuj użyć funkcji z bazy danych
      try {
        // Walidacja parametrów
        if (!userId || typeof userId !== 'string') {
          throw new Error('Invalid userId parameter');
        }
        if (!type || typeof type !== 'string') {
          throw new Error('Invalid type parameter');
        }

        const { data: funcResult, error: funcError } = await supabase.rpc('create_notification', {
          p_user_id: userId,
          p_type: type,
          p_data: data || {},
          p_event_id: eventId || null,
          p_application_id: applicationId || null
        });

        if (funcError) {
          console.error('create_notification RPC error:', funcError);
          console.error('Error details:', {
            code: funcError.code,
            message: funcError.message,
            details: funcError.details,
            hint: funcError.hint
          });
          console.error('Parameters:', { 
            userId, 
            type, 
            data: JSON.stringify(data), 
            eventId, 
            applicationId 
          });
          
          // Sprawdź różne możliwe przyczyny błędu 400
          if (funcError.code === '42883' || funcError.message?.includes('function') || funcError.message?.includes('does not exist')) {
            console.error('❌ BŁĄD: Funkcja create_notification nie istnieje lub ma nieprawidłową sygnaturę!');
            console.error('❌ Sprawdź czy funkcja istnieje w bazie danych.');
          } else if (funcError.code === 'P0001' || funcError.message?.includes('event_invitation')) {
            console.error('⚠️ Błąd: Typ powiadomienia "event_invitation" może nie być obsługiwany.');
            console.error('⚠️ Uruchom migrację: databases/038-add-event-invitation-notification-type.sql');
          } else if (funcError.code === '23514' || funcError.message?.includes('check constraint')) {
            console.error('❌ BŁĄD: Naruszenie constraint - sprawdź czy typ powiadomienia jest dozwolony.');
          } else if (funcError.code === '42804' || funcError.message?.includes('type mismatch')) {
            console.error('❌ BŁĄD: Nieprawidłowy typ parametru - sprawdź typy danych.');
          }
          
          throw funcError; // Rzuć błąd żeby przejść do fallback
        }

        if (funcResult) {
          notificationId = funcResult;
        } else {
          // Funkcja zwróciła NULL - może powiadomienie jest wyłączone w ustawieniach
          console.warn('create_notification returned NULL - notification may be disabled in settings');
          return {
            success: false,
            skipped: true,
            reason: 'Notification disabled in user settings',
            error: null
          };
        }
      } catch (funcError) {
        // Fallback: bezpośredni INSERT jeśli funkcja nie działa
        // UWAGA: To prawdopodobnie nie zadziała z powodu RLS (błąd 403)
        // Ale próbujemy jako fallback
        console.warn('create_notification RPC failed, trying direct INSERT (may fail due to RLS):', funcError);
        
        const { data: insertData, error: insertError } = await supabase
          .from('notifications')
          .insert({
            user_id: userId,
            type,
            data,
            event_id: eventId,
            application_id: applicationId,
            read: false
          })
          .select('id')
          .single();

        if (insertError) {
          // Błąd 403 jest oczekiwany - RLS blokuje bezpośredni INSERT
          // Główny problem to błąd funkcji RPC (400)
          if (insertError.code === '42501' || insertError.message?.includes('permission denied')) {
            console.error('❌ Direct INSERT blocked by RLS (expected). Main issue is RPC function error.');
            console.error('❌ Please run migration: databases/038-add-event-invitation-notification-type.sql');
          }
          
          return { 
            success: false, 
            error: funcError, // Zwróć oryginalny błąd RPC, nie błąd INSERT
            skipped: false
          };
        }

        notificationId = insertData.id;
      }

      // Oznacz że powiadomienie zostało wysłane
      if (!skipCooldown) {
        markNotificationSent(userId, type, uniqueKey);
      }

      return {
        success: true,
        notificationId,
        skipped: false
      };
    } catch (error) {
      console.error('Error creating notification:', error);
      return {
        success: false,
        error,
        skipped: false
      };
    }
  }, [cooldownMinutes, canSendNotification, markNotificationSent]);

  return createNotification;
}
